"use client";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";

type SocialPlatform = "instagram" | "facebook" | "tiktok" | "youtube";

interface SocialStatusBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  status: string;
  platform: SocialPlatform;
}

const platformTranslationPrefix: Record<SocialPlatform, string> = {
  instagram: "instagram.status",
  facebook: "instagram.facebook.status",
  tiktok: "tiktok.status",
  youtube: "youtube.status",
};

const statusVariants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  pending: "secondary",
  uploading: "default",
  publishing: "default",
  published: "outline",
  scheduled: "secondary",
  failed: "destructive",
  skipped: "secondary",
};

function SocialStatusBadge({ status, platform, className, ...props }: SocialStatusBadgeProps) {
  const { t } = useTranslation();
  const prefix = platformTranslationPrefix[platform];
  const variant = statusVariants[status] ?? "secondary";
  const label = t(`${prefix}.${status}`);

  return (
    <Badge
      variant={variant}
      className={cn(
        (status === "uploading" || status === "publishing") && "animate-pulse",
        status === "published" && "bg-green-100 text-green-800 hover:bg-green-100",
        "2k:text-base 2k:px-3 2k:py-1",
        "4k:text-xl 4k:px-5 4k:py-2",
        className
      )}
      {...props}
    >
      {label}
    </Badge>
  );
}

export { SocialStatusBadge };
